import { ref } from 'vue'
import { tryOnScopeDispose } from '@vueuse/core'

import {
  isRateLimitError,
  rateLimitedFor,
  serverErrorMessage
} from '@/shared/api'

import { showGlobalError } from './globalErrorDisplay'

const POLL_INTERVAL_MS = 2000
const MAX_POLLING_MS = 5 * 60 * 1000

/**
 * How long to wait before asking again when a poll is refused for asking too often.
 *
 * The cooldown's own remaining time, plus a little, so the next question lands after the
 * window rather than into it — a poll fired one millisecond early is bounced locally and
 * buys nothing. The floor is the normal interval, for a refusal that carries no window.
 */
function backoffMs(): number {
  return Math.max(POLL_INTERVAL_MS, rateLimitedFor() + 250)
}

interface RespondedJobCallbacks<TJob> {
  onProgress?: (id: string) => void
  /** The backend's own sentence about what went wrong — show it, don't log it. */
  onError?: (msg: string, args?: unknown) => void
  onSuccess?: (job: TJob) => void
}

/** What one poll of a job has to answer, whatever shape the endpoint returns it in. */
interface RespondedJobPoll<TJob> {
  id: string
  done: boolean
  error: string | null
  args?: unknown
  job: TJob
}

/**
 * The polling loop behind a "responded job": create it (or pick up one somebody else
 * created), then ask every two seconds until it is done or five minutes have passed,
 * surfacing the backend's own error sentence when there is one.
 *
 * Not called from a slice: `useRespondedJob` is this loop already wired to the v2 endpoint.
 * The transport is a parameter only so a differently shaped job endpoint can reuse
 * the loop.
 */
export function createRespondedJobPoller<TJob>(transport: {
  create: (type: string, args: Record<string, unknown>) => Promise<number>
  fetch: (id: number) => Promise<RespondedJobPoll<TJob>>
}) {
  const jobId = ref<number | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const startedAt = ref<number | null>(null)

  let timer: ReturnType<typeof setTimeout> | null = null
  let generation = 0

  function clear() {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  /**
   * Stop following the job. The generation counter is what makes it stick: a request
   * already in flight resolves after the timer is gone, and without it that answer
   * would still fire the callbacks of a screen nobody is looking at.
   */
  function cancel() {
    generation += 1
    clear()
    isLoading.value = false
  }

  // Otherwise a modal closed mid-poll keeps asking for the full five minutes.
  tryOnScopeDispose(cancel)

  function start() {
    clear()
    generation += 1
    isLoading.value = true
    error.value = null
    startedAt.value = Date.now()
  }

  async function run(
    type: string,
    args: Record<string, unknown>,
    options?: RespondedJobCallbacks<TJob>
  ) {
    start()

    try {
      const jid = await transport.create(type, args)

      jobId.value = jid
      options?.onProgress?.(String(jid))
      check(options)
    } catch (e) {
      console.error(e)
      // The backend's own sentence, never a constant standing in for it: `onError` promises
      // the sentence the backend wrote, and a caller writes `message.error(msg || '…')`, so
      // a constant here is always truthy, always wins, and hides the reason for
      // the refusal. Read it here and hand `finalizeError` the sentence, not the exception:
      // `showGlobalError` prefers `input.message` when handed an `Error`, and an
      // `AxiosError` is one — passing `e` shows "Request failed with status code 422".
      finalizeError(
        options,
        serverErrorMessage(e, 'Could not start the operation.'),
        args
      )
    }
  }

  /** Follow a job someone else created — `jid` from another endpoint's `meta`. */
  function poll(jid: number, options?: RespondedJobCallbacks<TJob>) {
    start()
    jobId.value = jid
    options?.onProgress?.(String(jid))
    check(options)
  }

  function finalizeError(
    options: RespondedJobCallbacks<TJob> | undefined,
    message: string,
    args?: unknown
  ) {
    isLoading.value = false
    clear()
    error.value = message
    if (!options?.onError)
      showGlobalError(message, 'Operation failed. Please try again later.')
    options?.onError?.(message, args)
  }

  async function check(options?: RespondedJobCallbacks<TJob>) {
    // A poller with nothing to follow has failed, not finished, and has to clear
    // `isLoading` like every other exit — otherwise a falsy job id freezes every control
    // that flag disables with no error to say why. `Number('abc')` is `NaN` and
    // `Number('0')` is `0`; both land here.
    if (!jobId.value) {
      finalizeError(options, 'Could not follow that operation.')
      return
    }

    const runId = generation

    if (startedAt.value && Date.now() - startedAt.value >= MAX_POLLING_MS) {
      finalizeError(options, 'Operation timed out after 5 minutes.')
      return
    }

    try {
      const polled = await transport.fetch(jobId.value)

      if (runId !== generation) return

      if (!polled.done) {
        options?.onProgress?.(polled.id)
        timer = setTimeout(() => check(options), POLL_INTERVAL_MS)
        return
      }

      if (polled.error) {
        finalizeError(options, polled.error, polled.args)
        return
      }

      isLoading.value = false
      clear()
      options?.onSuccess?.(polled.job)
    } catch (e) {
      if (runId !== generation) return

      /**
       * Being told to ask less often is not the job failing: it is still running, and its
       * result is still coming. Giving up here would report "lost track of the operation",
       * the user would start the work again, and the second run spends more of the very
       * budget the refusal is protecting. Waiting out the window costs nothing.
       *
       * `MAX_POLLING_MS` still ends it, so this cannot wait forever.
       */
      if (isRateLimitError(e)) {
        timer = setTimeout(() => check(options), backoffMs())
        return
      }

      console.error(e)
      finalizeError(
        options,
        serverErrorMessage(e, 'Lost track of the operation. Please try again.')
      )
    }
  }

  return { run, poll, cancel, isLoading, error, jobId }
}
